import React, { FC, useState } from "react";
import Sticker from "../../../components/elements/sticker/Sticker";
import FlexLine from "../../../components/elements/flexLine/FlexLine";
import { Align, ApiSelect, Rotate, TextType, Toggler } from "@baseTypes/baseType";
import ChartHeader from "../../../components/elements/chart/ChartHeader";
import Container from "../../../components/elements/container/Container";
import LinearChart from "../../../components/modules/dashboards/сharts/defaultCharts/linearChart/LinearChart";
import * as Highcharts from "highcharts";
import Tx from "../../../types/naming";
import { useTypedSelector } from "@hooks/useTypedSelector";
import { stickerLoadingWithCrop } from "../phases";
import { CircleLabel } from "../works/detalization/dynamics";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../store";
import { identity } from "fp-ts/es6/function";
import { NDVIstate } from "../../../store/reducers/dashboard/ndvi/types";
import Scrollbar from "../../../utils/highchart/Scrollbar";
import { Empty } from "./empty";
import { applyTip } from "@utils/utils";
import { formatNumber2d } from "@utils/format/number";
import BaseChartTooltipWithHeader, {
    HeaderTypes
} from "@modules/dashboards/dashboardTooltips/TooltipVariants/BaseChartTooltipWithHeader/BaseChartTooltipWithHeader";
import { formatDate, formatDateWithTemplate } from "@utils/format/date";
import Legend from "@elements/Legend/Legend";
import { isEmpty } from "lodash";
import colors from "@infobis/ui-styles-utils/colors";

interface INDVIdashboard {
    baseCropId: string;
}

const order: Record<string, number> = {
    'ndvi': 1,
    'ndwi': 2,
    'msi': 3,
    'gli': 4
}

const ndviSelector: ApiSelect<NDVIstate> =
    createSelector((state: RootState) => state.ndvi, identity<NDVIstate>);

const NDVIdashboard: FC<INDVIdashboard> = (props) => {
    const {
        data
    } = useTypedSelector(ndviSelector)

    const [chartApi, setChartApi] = useState<Highcharts.Chart>()

    const { selected } = useTypedSelector(state => state.cropSelector);

    const formatDateForDatePeriod = (dateMs: number) => {
        return formatDateWithTemplate(dateMs, 'DD MMM').replace('.', '')
    }

    const chartOptions = {
        chart: {
            spacing: [0, 0, 0, 0],
            margin: 0,
            marginBottom: 40,
            zooming: {
                type: 'x',
                // @ts-ignore
                mouseWheel: {
                    enabled: false
                },
                resetButton: {
                    theme: {
                        zIndex: 99
                    }
                }
            },
            events: {
                redraw: function () {
                    const min = this.xAxis[0]?.min || 0
                    const max = this.xAxis[0]?.max || 0

                    // @ts-ignore
                    this.legendDates?.destroy()

                    // @ts-ignore
                    this.legendDates = this.renderer
                        .text(`${formatDateForDatePeriod(min)} — ${formatDateForDatePeriod(max)}`, 0, this.chartHeight - 5)
                        .css({ color: colors.TYPO_SECONDARY, fontSize: '16px', lineHeight: '24px' })
                        .add();
                }
            }
        },
        title: {
            text: ''
        },
        xAxis: {
            type: 'datetime',
            labels: {
                enabled: false
            },
            scrollbar: Scrollbar({ margin: 5 }),
            gridLineWidth: 0,
            tickColor: 'transparent',
            lineColor: 'transparent'
        },
        yAxis: {
            gridLineWidth: 0,
            tickColor: 'transparent',
            lineColor: 'transparent',
            labels: {
                enabled: false,
            },
            title: { text: "" },
            tickPositioner: function () {
                const positions = [];
                const min = this.min || 0;
                const max = this.max || 0;

                const step = (max - min) / 5;
                for (let i = 0; i <= 5; i++) {
                    positions.push(min + i * step);
                }

                return positions;
            },
        },
        tooltip: applyTip(
            ctx => <BaseChartTooltipWithHeader
                header={{ type: HeaderTypes.ndvi, title: formatDate(new Date(ctx.x as number)) }}
                elements={
                    ctx.points?.map((point, id) => ({
                        id: `${point.series.name}-${id}`,
                        name: point.series.name,
                        // @ts-ignore
                        color: point.color as string,
                        value: formatNumber2d(point.y)
                    })) || []
                }
                width={120}
            />,
            {
                shared: true,
                outside: false,
            }
        ),
        credits: {
            enabled: false
        },
        legend: {
            enabled: false
        },
        series: data.map((el) => ({
            id: el.id,
            data: el.values.slice(),
            color: el.color,
            type: "spline",
            marker: { enabled: false },
            name: el.name,
        })),
    } as Highcharts.Options;


    const renderBody = () => <>
        <FlexLine textType={TextType.Header} align={Align.SpaceBetween}>
            <ChartHeader>{Tx.NDVI}</ChartHeader>
            <Legend rotate={Rotate.Row}
                    gap={15}
                    chartApi={chartApi}
                    toggler={Toggler.series}
                    rows={data.map(el => ({
                        id: el.id,
                        color: el.color,
                        values: [<CircleLabel size={'l'} color={el.color} name={el.name}></CircleLabel>]
                    })).sort((a, b) => (order[a.id] || 5) - (order[b.id] || 6))}
            />
        </FlexLine>
        <Container>
            <LinearChart
                isStock
                callback={setChartApi}
                options={chartOptions}
                zoomAxis={'xAxis'}
            ></LinearChart>
        </Container>
    </>
    return <Sticker>
        {!isEmpty(data) && selected && !isEmpty(selected) ? renderBody() : <Empty/>}
    </Sticker>
}


export default stickerLoadingWithCrop(
    ndviSelector,
    actions => actions.fetchNdvi
)(NDVIdashboard);